/**
 * O estado do lançamento contínuo — o reducer do contrato.
 *
 * O modelo vem do que o sistema de hoje já faz, não de invenção: **um circuito,
 * várias ligações**. O `ReplicarCampos()` do `Pinout.incluir.js` acrescenta
 * grupos de (componente, conector, pino, tipo) para o mesmo circuito, e o
 * `PinoutFormIncluir()` monta um `pinouts[]` que vai num POST só.
 *
 * Os **alfinetes** também vêm de lá. O `LimparPinoutParcial()` limpa apenas
 * `circuito`, `cor` e `pino` depois de gravar — tudo o mais fica. A diferença é
 * que aqui isso é visível e destravável, em vez de um comportamento que a pessoa
 * descobre por tentativa.
 */

import { VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"
import type { PinoutResponse } from "@/api/tipos"

/** O que vale para o circuito inteiro. */
export type Cabecalho = {
  circuito: ValorBusca
  /**
   * O tipo do circuito escolhido aqui (Positivo 12V, Linha 15, Sinal…), ou
   * `null` quando a pessoa não mexeu — aí vale o que o circuito já tem, e nada
   * vai para a API. É o mesmo campo do cadastro de circuitos, trazido para o
   * lançamento para não obrigar a ir até lá depois.
   */
  circuitoTipo: number | null
  cor: ValorBusca
  chicote: ValorBusca
  restricao: ValorBusca
  obs: string
  arquivoOriginal: string
}

/** Uma ligação: onde este circuito encosta. */
export type Ligacao = {
  /** Chave local, só para o React. Não vai para a API. */
  chave: string
  componente: ValorBusca
  conector: ValorBusca
  pino: string
  tipo: number
}

export type CampoFixavel = "componente" | "conector" | "chicote" | "restricao" | "arquivoOriginal" | "tipo"

/**
 * Quais campos ficam preenchidos depois de gravar.
 *
 * O padrão reproduz o `LimparPinoutParcial()`: limpa circuito, cor e pino;
 * mantém componente, conector, chicote, restrição, arquivo e tipo. A pessoa pode
 * destravar qualquer um clicando no alfinete.
 */
export const FIXOS_PADRAO: Record<CampoFixavel, boolean> = {
  componente: true,
  conector: true,
  chicote: true,
  restricao: true,
  arquivoOriginal: true,
  tipo: true,
}

export type EstadoLancamento = {
  cabecalho: Cabecalho
  ligacoes: Ligacao[]
  fixos: Record<CampoFixavel, boolean>
  /** Fica marcado ao gravar; a barra de estado usa para o desfazer. */
  ultimoLote: { idsCriados: number[]; criados: number; ignorados: number; quando: number } | null
  mensagens: { indice: number; campo: string; texto: string }[]
  continuar: boolean
  /**
   * Id do pinout em edição, ou `null` para lançamento novo.
   *
   * Editar e lançar são a mesma tela porque no sistema de hoje também são: o
   * `Incluir` do `PinoutController` recebe `pinout_id` e reabre o mesmo
   * formulário preenchido. Reaproveitar a tela dá de graça a cadeia de foco, os
   * comboboxes com get-or-create e a validação — e é o que a equipe já tem no
   * dedo.
   *
   * Editando, a linha é uma só: um pinout é UMA ligação de um circuito, então
   * "mais uma ligação" e "continuar cadastrando" não fazem sentido e somem.
   */
  editando: number | null
}

let contador = 0
function novaChave(): string {
  contador += 1
  return `l${contador}`
}

export function ligacaoVazia(): Ligacao {
  return { chave: novaChave(), componente: VAZIO, conector: VAZIO, pino: "", tipo: 0 }
}

export function estadoInicial(): EstadoLancamento {
  return {
    cabecalho: { circuito: VAZIO, circuitoTipo: null, cor: VAZIO, chicote: VAZIO, restricao: VAZIO, obs: "", arquivoOriginal: "" },
    ligacoes: [ligacaoVazia()],
    fixos: { ...FIXOS_PADRAO },
    ultimoLote: null,
    mensagens: [],
    continuar: true,
    editando: null,
  }
}

/**
 * Monta o formulário a partir de um pinout que já existe.
 *
 * Tudo entra como `vinculado`: os ids vieram do banco, e marcar como `novo`
 * faria a gravação tentar criar de novo o componente que já está lá.
 */
export function estadoDeEdicao(p: PinoutResponse): EstadoLancamento {
  const vinculado = (id: number | null | undefined, texto: string | null | undefined): ValorBusca =>
    id && id > 0 ? { estado: "vinculado", id, texto: texto ?? "" } : VAZIO

  return {
    cabecalho: {
      circuito: vinculado(p.circuitoId, p.circuitoDescricao),
      circuitoTipo: null,
      cor: vinculado(p.corId, p.corDescricao),
      chicote: vinculado(p.chicoteId, p.chicoteDescricao),
      // A restrição do pinout vem como lista; o formulário guarda uma.
      restricao: vinculado(p.restricoes?.[0]?.id, p.restricoes?.[0]?.descricao),
      obs: p.obs ?? "",
      arquivoOriginal: p.arquivoOriginal ?? "",
    },
    ligacoes: [{
      chave: novaChave(),
      componente: vinculado(p.componenteId, p.componenteCodigo || p.componenteDescricao),
      conector: vinculado(p.conectorId, p.conectorDescricao),
      pino: p.pino ?? "",
      tipo: p.tipo ?? 0,
    }],
    fixos: { ...FIXOS_PADRAO },
    ultimoLote: null,
    mensagens: [],
    continuar: false,
    editando: p.id,
  }
}

/**
 * Um passo da duplicação: o formulário pronto e, para cada ligação, o
 * componente e o conector que ela tinha na origem — é o que permite levar a
 * troca feita aqui para os passos seguintes.
 */
export type PassoDeDuplicacao = {
  estado: EstadoLancamento
  origem: Record<string, { componenteId: number; conectorId: number }>
}

/**
 * Os pinouts marcados, prontos para lançar de novo como pinouts novos.
 *
 * **Um passo por circuito.** O lançamento é um circuito com as suas ligações,
 * e os pinouts de um componente costumam estar em circuitos diferentes. Então
 * as linhas se agrupam pelo que é do cabeçalho — circuito, cor, chicote,
 * restrição, observação e fonte — e cada grupo vira um formulário, com as
 * linhas dele como ligações. Nada é gravado até a pessoa gravar cada passo.
 */
export function filaDeDuplicacao(pinouts: PinoutResponse[]): PassoDeDuplicacao[] {
  const grupos = new Map<string, PinoutResponse[]>()
  for (const p of pinouts) {
    const chave = [p.circuitoId, p.corId, p.chicoteId, p.restricoes?.[0]?.id ?? 0, p.obs ?? "", p.arquivoOriginal ?? ""].join("|")
    const g = grupos.get(chave)
    if (g) g.push(p)
    else grupos.set(chave, [p])
  }

  return [...grupos.values()].map((g) => {
    const base = estadoDeEdicao(g[0]!)
    const ligacoes = g.map((p) => ({ ...estadoDeEdicao(p).ligacoes[0]! }))
    const origem = Object.fromEntries(ligacoes.map((l, i) => [l.chave, {
      componenteId: g[i]!.componenteId, conectorId: g[i]!.conectorId,
    }]))
    return { estado: { ...base, ligacoes, editando: null, continuar: false }, origem }
  })
}

/**
 * Leva para o próximo passo as trocas feitas neste: se a pessoa trocou o
 * componente X por Y (ou o conector), as ligações seguintes que eram de X já
 * chegam em Y. É o "mudar só o que é diferente" — trocar o componente uma vez,
 * não uma vez por circuito.
 */
export type Trocas = { componentes: Map<number, ValorBusca>; conectores: Map<number, ValorBusca> }

export function registrarTrocas(trocas: Trocas, passo: PassoDeDuplicacao, ligacoes: Ligacao[]): Trocas {
  const componentes = new Map(trocas.componentes)
  const conectores = new Map(trocas.conectores)
  for (const l of ligacoes) {
    const o = passo.origem[l.chave]
    if (!o) continue
    if (!(l.componente.estado === "vinculado" && l.componente.id === o.componenteId)) componentes.set(o.componenteId, l.componente)
    if (!(l.conector.estado === "vinculado" && l.conector.id === o.conectorId)) conectores.set(o.conectorId, l.conector)
  }
  return { componentes, conectores }
}

export function aplicarTrocas(passo: PassoDeDuplicacao, trocas: Trocas): EstadoLancamento {
  return {
    ...passo.estado,
    ligacoes: passo.estado.ligacoes.map((l) => {
      const o = passo.origem[l.chave]
      if (!o) return l
      return {
        ...l,
        componente: trocas.componentes.get(o.componenteId) ?? l.componente,
        conector: trocas.conectores.get(o.conectorId) ?? l.conector,
      }
    }),
  }
}

export type Acao =
  | { tipo: "cabecalho"; campo: Exclude<keyof Cabecalho, "circuitoTipo">; valor: ValorBusca | string }
  | { tipo: "circuitoTipo"; valor: number | null }
  | { tipo: "ligacao"; chave: string; campo: keyof Omit<Ligacao, "chave">; valor: ValorBusca | string | number }
  | { tipo: "adicionarLigacao" }
  | { tipo: "removerLigacao"; chave: string }
  | { tipo: "alfinete"; campo: CampoFixavel }
  | { tipo: "continuar"; valor: boolean }
  | { tipo: "gravou"; idsCriados: number[]; criados: number; ignorados: number; mensagens: EstadoLancamento["mensagens"] }
  | { tipo: "limparMensagens" }
  | { tipo: "desfez" }
  | { tipo: "zerar" }
  | { tipo: "carregar"; estado: EstadoLancamento }

export function reduzir(e: EstadoLancamento, a: Acao): EstadoLancamento {
  switch (a.tipo) {
    case "cabecalho": {
      const cabecalho = { ...e.cabecalho, [a.campo]: a.valor } as Cabecalho
      // Trocou o circuito: o tipo escolhido era do outro.
      if (a.campo === "circuito") cabecalho.circuitoTipo = null
      return { ...e, cabecalho }
    }

    case "circuitoTipo":
      return { ...e, cabecalho: { ...e.cabecalho, circuitoTipo: a.valor } }

    case "ligacao":
      return {
        ...e,
        ligacoes: e.ligacoes.map((l) => (l.chave === a.chave ? { ...l, [a.campo]: a.valor } : l)),
      }

    case "adicionarLigacao": {
      // Um pinout é uma ligação. Editando, Ctrl+D não tem o que acrescentar.
      if (e.editando != null) return e
      // A ligação nova herda o que está alfinetado da última — é o
      // `ReplicarCampos()` do legado, só que respeitando os alfinetes.
      const ultima = e.ligacoes[e.ligacoes.length - 1]
      const nova = ligacaoVazia()
      if (ultima) {
        if (e.fixos.componente) nova.componente = ultima.componente
        if (e.fixos.conector) nova.conector = ultima.conector
        if (e.fixos.tipo) nova.tipo = ultima.tipo
      }
      return { ...e, ligacoes: [...e.ligacoes, nova] }
    }

    case "removerLigacao": {
      // Nunca fica sem nenhuma: remover a última deixa uma em branco no lugar.
      const restantes = e.ligacoes.filter((l) => l.chave !== a.chave)
      return { ...e, ligacoes: restantes.length > 0 ? restantes : [ligacaoVazia()] }
    }

    case "alfinete":
      return { ...e, fixos: { ...e.fixos, [a.campo]: !e.fixos[a.campo] } }

    case "continuar":
      return { ...e, continuar: a.valor }

    case "gravou": {
      const lote = {
        idsCriados: a.idsCriados, criados: a.criados, ignorados: a.ignorados, quando: Date.now(),
      }
      // Continuar desmarcado: grava e para, mantendo o que está na tela para
      // conferência. Marcado: limpa o que o legado limpa e segue.
      if (!e.continuar) return { ...e, ultimoLote: lote, mensagens: a.mensagens }

      const ultima = e.ligacoes[e.ligacoes.length - 1]
      const proxima = ligacaoVazia()
      if (ultima) {
        if (e.fixos.componente) proxima.componente = ultima.componente
        if (e.fixos.conector) proxima.conector = ultima.conector
        if (e.fixos.tipo) proxima.tipo = ultima.tipo
      }

      return {
        ...e,
        cabecalho: {
          // Limpa circuito e cor — o `LimparPinoutParcial()`.
          circuito: VAZIO,
          circuitoTipo: null,
          cor: VAZIO,
          chicote: e.fixos.chicote ? e.cabecalho.chicote : VAZIO,
          restricao: e.fixos.restricao ? e.cabecalho.restricao : VAZIO,
          obs: e.cabecalho.obs,
          arquivoOriginal: e.fixos.arquivoOriginal ? e.cabecalho.arquivoOriginal : "",
        },
        ligacoes: [proxima],
        ultimoLote: lote,
        mensagens: a.mensagens,
      }
    }

    case "limparMensagens":
      return { ...e, mensagens: [] }

    case "desfez":
      return { ...e, ultimoLote: null }

    case "zerar":
      return { ...estadoInicial(), fixos: e.fixos, continuar: e.continuar }

    case "carregar":
      return a.estado
  }
}

// ------------------------------------------------------------------ validação

export type Pendencia = { onde: string; texto: string }

/**
 * O que impede a gravação. Deliberadamente curto: o resto a API valida, e
 * duplicar regra aqui só cria dois lugares para errar.
 */
export function pendencias(e: EstadoLancamento): Pendencia[] {
  const p: Pendencia[] = []
  if (e.cabecalho.circuito.estado === "vazio") p.push({ onde: "circuito", texto: "Informe o circuito." })

  e.ligacoes.forEach((l, i) => {
    const temAlgo = l.componente.estado !== "vazio" || l.pino.trim() !== ""
    if (!temAlgo) return
    if (l.componente.estado === "vazio") p.push({ onde: `ligacao-${i}`, texto: `Ligação ${i + 1}: falta o componente.` })
    if (l.pino.trim() === "") p.push({ onde: `ligacao-${i}`, texto: `Ligação ${i + 1}: falta o pino.` })
  })

  if (e.ligacoes.every((l) => l.componente.estado === "vazio" && l.pino.trim() === ""))
    p.push({ onde: "ligacoes", texto: "Informe ao menos uma ligação." })

  return p
}

/** Quantas linhas o POST vai levar — o número que o botão mostra. */
export function quantasLigacoes(e: EstadoLancamento): number {
  return e.ligacoes.filter((l) => l.componente.estado !== "vazio" && l.pino.trim() !== "").length
}
